import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Check, ChevronDown, RotateCcw, Search } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigationType, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { DataState, formatMoney, formatTime, PageHeader, StatusBadge } from "../../components/ui";
import { localDateTimeToUnix, toLocalDateTimeInput, validateUnixRange } from "../../utils/time";

const DAY = 24 * 60 * 60;

// RequestsPage provides paginated troubleshooting with optional time filters.
// 请求排障页默认不限时间，按页查询并支持主动选择时间范围。
export function RequestsPage() {
  const [params, setParams] = useSearchParams();
  const navigationType = useNavigationType();
  const appliedStart = params.get("start_at") || "";
  const appliedEnd = params.get("end_at") || "";
  const hasTimeFilter = params.has("start_at") || params.has("end_at");
  const [draft, setDraft] = useState<Record<string, string>>(() => ({
    start_at: appliedStart, end_at: appliedEnd, limit: "50",
    ...Object.fromEntries([...params].filter(([key]) => key !== "consumer_key_id")),
  }));
  const [customTimeOpen, setCustomTimeOpen] = useState(() => hasTimeFilter && !presetDays(Number(appliedStart), Number(appliedEnd)));
  const consumers = useQuery({ queryKey: ["consumers"], queryFn: api.consumers });
  const models = useQuery({ queryKey: ["models"], queryFn: api.models });
  const selectedConsumer = consumers.data?.data.find((item) => item.id === Number(draft.consumer_id));
  const actualChannels = [...new Map((models.data?.data || []).flatMap((item) => item.channels).map((channel) => [channel.id, channel])).values()];
  const queryString = useMemo(() => {
    const next = new URLSearchParams(params);
    next.delete("consumer_key_id");
    if (!next.has("limit")) next.set("limit", "50");
    return next.toString();
  }, [params]);
  const requests = useQuery({ queryKey: ["requests", queryString], queryFn: () => api.requests(queryString) });
  const requestItems = requests.data?.items || [];

  useEffect(() => {
    if (params.has("limit") && !params.has("consumer_key_id")) return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("consumer_key_id");
      if (!next.has("limit")) next.set("limit", "50");
      return next;
    }, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (navigationType !== "POP") return;
    setDraft({ start_at: "", end_at: "", limit: "50", ...Object.fromEntries([...params].filter(([key]) => key !== "consumer_key_id")) });
    setCustomTimeOpen(hasTimeFilter && !presetDays(Number(appliedStart), Number(appliedEnd)));
  }, [params, navigationType, hasTimeFilter, appliedStart, appliedEnd]);

  const updateDraft = (key: string, value: string) => setDraft((current) => {
    const next = { ...current, [key]: value };
    if (key === "consumer_id") {
      next.consumer_business_id = "";
    }
    if (key === "requested_model_id") next.executed_channel_id = "";
    return next;
  });
  const updateSelection = (key: string, value: string) => {
    updateDraft(key, value);
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "consumer_id") {
      next.delete("consumer_business_id");
    }
    if (key === "requested_model_id") next.delete("executed_channel_id");
    next.delete("cursor");
    setParams(next, { replace: true });
  };
  const submitRequestId = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    const value = (draft.request_id || "").trim();
    if (value) next.set("request_id", value); else next.delete("request_id");
    next.delete("cursor");
    setParams(next);
  };
  const clearRequestId = () => {
    updateDraft("request_id", "");
    const next = new URLSearchParams(params);
    next.delete("request_id");
    next.delete("cursor");
    setParams(next, { replace: true });
  };
  const usePreset = (days: number) => {
    const range = { start_at: String(Math.floor(Date.now() / 1000) - days * DAY), end_at: String(Math.floor(Date.now() / 1000) + 1) };
    setDraft((current) => ({ ...current, ...range }));
    setCustomTimeOpen(false);
    const next = new URLSearchParams(params);
    next.set("start_at", range.start_at);
    next.set("end_at", range.end_at);
    next.delete("cursor");
    setParams(next, { replace: true });
  };
  const clearTime = () => {
    const next = new URLSearchParams(params);
    next.delete("start_at");
    next.delete("end_at");
    next.delete("cursor");
    setDraft((current) => ({ ...current, start_at: "", end_at: "" }));
    setCustomTimeOpen(false);
    setParams(next, { replace: true });
  };
  const applyTime = () => {
    const next = new URLSearchParams(params);
    next.set("start_at", draft.start_at);
    next.set("end_at", draft.end_at);
    next.delete("cursor");
    setParams(next, { replace: true });
  };
  const applyAdvancedInputs = () => {
    const next = new URLSearchParams(params);
    advancedInputKeys.forEach((key) => {
      const value = (draft[key] || "").trim();
      if (value) next.set(key, value); else next.delete(key);
    });
    next.delete("cursor");
    setParams(next, { replace: true });
  };
  const clear = () => {
    const next = { limit: "50" };
    setDraft({ ...next, start_at: "", end_at: "" });
    setCustomTimeOpen(false);
    setParams(next);
  };
  const activePresetDays = customTimeOpen ? 0 : presetDays(Number(appliedStart), Number(appliedEnd));
  const timeDirty = draft.start_at !== String(appliedStart) || draft.end_at !== String(appliedEnd);
  const timeError = validateUnixRange(draft.start_at, draft.end_at, 31);
  const advancedDirty = advancedInputKeys.some((key) => (draft[key] || "").trim() !== (params.get(key) || ""));

  return <>
    <PageHeader title="请求排障" description="按业务、模型、错误和输入输出引用定位请求，再进入详情查看完整执行路径。" />
    <form className="request-id-panel" onSubmit={submitRequestId}>
      <label className="search-control request-id-search"><Search size={16} /><input value={draft.request_id || ""} onChange={(event) => updateDraft("request_id", event.target.value)} placeholder="输入完整 Request ID" aria-label="Request ID" /></label>
      <button className="primary-button"><Search size={16} />精确查询</button>
      {(draft.request_id || params.has("request_id")) ? <button type="button" className="secondary-button" onClick={clearRequestId}>清空</button> : null}
    </form>
    <section className="request-filter-panel">
      <div className="filter-panel-heading">
        <strong>组合筛选</strong>
        <button type="button" className="secondary-button compact-action" onClick={clear}><RotateCcw size={15} />重置筛选</button>
      </div>
      <div className="request-time-row">
        <div className="filter-choice-group"><span>时间范围</span><div className="segmented-control" aria-label="请求时间范围">
          <button type="button" aria-pressed={!hasTimeFilter && !customTimeOpen} className={!hasTimeFilter && !customTimeOpen ? "active" : ""} onClick={clearTime}>不限时间</button>
          {[{ label: "24 小时", days: 1 }, { label: "7 天", days: 7 }, { label: "30 天", days: 30 }].map((item) => <button type="button" key={item.days} aria-pressed={activePresetDays === item.days} className={activePresetDays === item.days ? "active" : ""} onClick={() => usePreset(item.days)}>{item.label}</button>)}
          <button type="button" aria-pressed={customTimeOpen} className={customTimeOpen ? "active" : ""} onClick={() => setCustomTimeOpen(true)}>自定义</button>
        </div></div>
        {customTimeOpen ? <div className="custom-time-range">
          <label className="compact-field"><span>开始时间</span><input type="datetime-local" value={toLocalDateTimeInput(draft.start_at)} onInput={(event) => updateDraft("start_at", localDateTimeToUnix(event.currentTarget.value))} /></label>
          <span className="range-separator">至</span>
          <label className="compact-field"><span>结束时间</span><input type="datetime-local" value={toLocalDateTimeInput(draft.end_at)} onInput={(event) => updateDraft("end_at", localDateTimeToUnix(event.currentTarget.value))} /></label>
          <button type="button" className={timeDirty && !timeError ? "primary-button" : "secondary-button"} disabled={!timeDirty || Boolean(timeError)} onClick={applyTime}><Check size={15} />{timeError || "应用时间"}</button>
        </div> : null}
      </div>
      <div className="request-dimension-grid">
        <FilterSelect label="状态" value={draft.status || ""} onChange={(value) => updateSelection("status", value)} options={[{ value: "queued", label: "排队中" }, { value: "running", label: "执行中" }, { value: "succeeded", label: "成功" }, { value: "failed", label: "失败" }, { value: "uncertain", label: "待确认" }]} />
        <FilterSelect label="业务方" value={draft.consumer_id || ""} onChange={(value) => updateSelection("consumer_id", value)} options={(consumers.data?.data || []).map((item) => ({ value: String(item.id), label: item.display_name }))} />
        <FilterSelect label="业务标识" value={draft.consumer_business_id || ""} onChange={(value) => updateSelection("consumer_business_id", value)} disabled={!selectedConsumer} options={(selectedConsumer?.businesses || []).map((item) => ({ value: String(item.id), label: item.business_identifier }))} />
        <FilterSelect label="请求模型" value={draft.requested_model_id || ""} onChange={(value) => updateSelection("requested_model_id", value)} options={(models.data?.data || []).map((item) => ({ value: String(item.id), label: item.display_name }))} />
        <FilterSelect label="最终渠道" value={draft.executed_channel_id || ""} onChange={(value) => updateSelection("executed_channel_id", value)} options={actualChannels.map((item) => ({ value: String(item.id), label: item.display_name }))} />
      </div>
      <details className="advanced-filters"><summary><ChevronDown size={15} />更多条件</summary><div className="request-dimension-grid advanced-grid">
        <TextFilter label="业务任务 ID" value={draft.business_task_id || ""} onChange={(value) => updateDraft("business_task_id", value)} />
        <TextFilter label="幂等 Key" value={draft.idempotency_key || ""} onChange={(value) => updateDraft("idempotency_key", value)} />
        <TextFilter label="生成任务 ID" value={draft.generation_job_id || ""} onChange={(value) => updateDraft("generation_job_id", value)} inputMode="numeric" />
        <TextFilter label="上游任务 ID" value={draft.provider_task_id || ""} onChange={(value) => updateDraft("provider_task_id", value)} />
        <FilterSelect label="类型" value={draft.modality || ""} onChange={(value) => updateSelection("modality", value)} options={[{ value: "image", label: "图片" }, { value: "video", label: "视频" }]} />
        <FilterSelect label="响应模式" value={draft.response_mode || ""} onChange={(value) => updateSelection("response_mode", value)} options={[{ value: "async", label: "异步" }, { value: "blocking", label: "阻塞" }, { value: "stream", label: "流式" }]} />
        <TextFilter label="错误类型" value={draft.final_error_kind || ""} onChange={(value) => updateDraft("final_error_kind", value)} />
        <TextFilter label="错误码" value={draft.final_error_code || ""} onChange={(value) => updateDraft("final_error_code", value)} />
        <TextFilter label="输入 OSS 引用" value={draft.request_ref || ""} onChange={(value) => updateDraft("request_ref", value)} />
        <TextFilter label="输出 OSS 引用" value={draft.result_ref || ""} onChange={(value) => updateDraft("result_ref", value)} />
      </div><div className="advanced-actions"><button type="button" className="primary-button" disabled={!advancedDirty} onClick={applyAdvancedInputs}><Search size={15} />应用输入条件</button></div></details>
    </section>
    <DataState loading={requests.isLoading || consumers.isLoading || models.isLoading} error={requests.error || consumers.error || models.error} empty={!requestItems.length} emptyTitle="没有匹配的请求" emptyDescription="检查时间范围和查询条件后重试">
      <div className="table-wrap"><table><thead><tr><th>Request ID</th><th>业务</th><th>状态</th><th>模型与渠道</th><th>尝试</th><th>费用</th><th>错误</th><th>创建时间</th><th aria-label="操作" /></tr></thead><tbody>{requestItems.map((item) => <tr key={item.id}><td><Link className="request-id-link" to={`/manage/requests/${encodeURIComponent(item.request_id)}`}><code>{item.request_id}</code></Link><small className="cell-note">{item.business_task_id || "无业务任务 ID"}</small></td><td><strong>{item.consumer_name || `#${item.consumer_id}`}</strong><small className="cell-note">{item.business_identifier || `业务 #${item.consumer_business_id}`}</small></td><td><StatusBadge value={item.status} /></td><td>{item.requested_model_key || `#${item.requested_model_id}`}<small className="cell-note">{item.executed_channel_name || "尚未选定渠道"}</small></td><td>{item.attempt_count}</td><td>{formatMoney(item.total_cost, item.currency)}</td><td>{item.final_error_kind || "-"}<small className="cell-note error-note">{item.final_error_code || item.final_error_message}</small></td><td>{formatTime(item.created_at)}</td><td><Link className="row-link" to={`/manage/requests/${encodeURIComponent(item.request_id)}`}>查看 <ArrowUpRight size={15} /></Link></td></tr>)}</tbody></table></div>
      <div className="cursor-actions"><span>本页 {requestItems.length} 条</span>{params.has("cursor") ? <button className="secondary-button" onClick={() => { const next = new URLSearchParams(params); next.delete("cursor"); setParams(next); }}>返回第一页</button> : null}<button className="primary-button" disabled={!requests.data?.has_more} onClick={() => { const next = new URLSearchParams(params); next.set("cursor", requests.data!.next_cursor); setParams(next); }}>下一页</button></div>
    </DataState>
  </>;
}

function FilterSelect({ label, value, options, disabled, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; disabled?: boolean; onChange: (value: string) => void }) {
  return <label className="compact-field"><span>{label}</span><select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}><option value="">全部</option>{options.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>;
}

function TextFilter({ label, value, inputMode, onChange }: { label: string; value: string; inputMode?: "numeric"; onChange: (value: string) => void }) {
  return <label className="compact-field"><span>{label}</span><input inputMode={inputMode} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

const advancedInputKeys = ["business_task_id", "idempotency_key", "generation_job_id", "provider_task_id", "final_error_kind", "final_error_code", "request_ref", "result_ref"];

function presetDays(startAt: number, endAt: number) {
  const range = endAt - startAt;
  return [1, 7, 30].find((days) => Math.abs(range - days * DAY) < 120) || 0;
}
